import React, { type ReactNode, useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

export const ConfluenceAifcAdfUpdateAction = ({
	onConfirm,
	onCancel,
	actions,
	getStatus,
	getReadonly,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.AifcAdfUpdate>): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const firstAction = actions[0];
	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const isDisabled = status !== 'idle' || readonly;
	const isSubmitting = status === 'started';

	const handleConfirm = useCallback(() => {
		if (fg('rovo_tools_resumption_migration_phase2')) {
			const plannedActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluenceAifcAdfUpdateRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					// Confirm the submitted action; cancel every other queued action.
					tools: plannedActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}
		onConfirm(firstAction);
	}, [
		onConfirm,
		firstAction,
		actions,
		sendMessageConfirmedTools,
		getStatus,
		recipientAgentId,
		conversationId,
		messageId,
	]);

	const handleCancel = useCallback(() => {
		if (fg('rovo_tools_resumption_migration_phase2')) {
			const plannedActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluenceAifcAdfUpdateRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: plannedActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}
		onCancel(firstAction);
	}, [
		onCancel,
		firstAction,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		getStatus,
	]);

	// In FE mode (no adf in data), edits are streamed directly to the editor
	// so no confirmation card is needed. Only render in backend mode where the
	// user must explicitly confirm before the hook applies changes.
	if (!data.adf) {
		return null;
	}

	return (
		<Stack space="space.250" testId="confluence-aifc-adf-update-action">
			<Text>
				{formatMessage(messages.actionMessage, {
					bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				})}
			</Text>
			<ActionCardWrapper title={formatMessage(messages.actionCardTitle)} product="confluence">
				<Box>
					<Text>{data.summary}</Text>
				</Box>
				<ActionCardFooter>
					<Inline space="space.100">
						<Button
							appearance="primary"
							isDisabled={isDisabled}
							isLoading={isSubmitting}
							onClick={handleConfirm}
							testId="confluence-aifc-adf-update-confirm-button"
						>
							{formatMessage(messages.confirmButton)}
						</Button>
						<Button
							appearance="subtle"
							isDisabled={isDisabled}
							onClick={handleCancel}
							testId="confluence-aifc-adf-update-cancel-button"
						>
							{formatMessage(messages.cancelButton)}
						</Button>
					</Inline>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};
